import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import axios from "axios";
import "./InfoPercorsi.css";
import RotateDevice from "../components/RotateDevice/RotateDevice";
import DatePickerModal from "../components/DatePickerModal/DatePickerModal";
import Lf23Loader from "../components/Lf23Loader";

const InfoPercorsi = () => {
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

    const [dati, setDati] = useState([]);
    const [paginaCorrente, setPaginaCorrente] = useState(1);
    const [pagineTotali, setPagineTotali] = useState(1);
    const [dettaglioAperto, setDettaglioAperto] = useState(null);
    const [loading, setLoading] = useState(false);
    const [limit, setLimit] = useState(20);
    const [totaleRisultati, setTotaleRisultati] = useState(0);
    const [showInfo, setShowInfo] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [viewMode, setViewMode] = useState("table");
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Escape key per uscire dal fullscreen
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [isFullscreen]);

    // Helper function to get default date range (from yesterday to today)
    const getDefaultDateRange = () => {
        const today = new Date();
        const yesterday = new Date(today);
        // yesterday.setDate(today.getDate() - 1);

        const formatDate = (date) => {
            return date.toISOString().split('T')[0]; // YYYY-MM-DD format
        };

        return {
            from: formatDate(yesterday),
            to: formatDate(today)
        };
    };

    const defaultDates = getDefaultDateRange();

    // Filtri
    const [fromDate, setFromDate] = useState(defaultDates.from);
    const [toDate, setToDate] = useState(defaultDates.to);
    const [percorso, setPercorso] = useState("");
    const [targa, setTarga] = useState("");
    const [stati, setStati] = useState(["Completato", "In lavorazione"]);
    const [sortKey, setSortKey] = useState("planning");
    const [sortDir, setSortDir] = useState("asc");
    const [sortKeyMain, setSortKeyMain] = useState("data");
    const [sortDirMain, setSortDirMain] = useState("desc");

    // Stats globali
    const [stats, setStats] = useState({
        totalPercorsi: 0,
        campaneTotali: 0,
        campaneSvuotate: 0,
        campaneDaSvuotare: 0
    });

    const fetchStats = async () => {
        try {
            const res = await axios.get(`${API_URL}/interacta/info-percorsi`, {
                params: {
                    page: 1,
                    limit: 999999,
                    fromDate: fromDate || undefined,
                    toDate: toDate || undefined,
                    nomePercorso: percorso,
                    targa,
                    stati: stati.join(","),
                    sortKey: sortKeyMain,
                    sortDir: sortDirMain,
                }
            });

            const allData = res.data?.data ?? [];
            const totalCampane = allData.reduce((sum, row) => sum + (parseInt(row.nr_campane_percorso) || 0), 0);
            const totalSvuotate = allData.reduce((sum, row) => sum + (parseInt(row.nr_campane_svuotate) || 0), 0);
            const totalDaSvuotare = allData.reduce((sum, row) => sum + (parseInt(row.nr_campane_da_svuotare) || 0), 0);

            setStats({
                totalPercorsi: res.data?.pagination?.totalItems || 0,
                campaneTotali: totalCampane,
                campaneSvuotate: totalSvuotate,
                campaneDaSvuotare: totalDaSvuotare
            });
        } catch (err) {
            console.error("Errore nel caricamento statistiche:", err);
        }
    };

    const fetchData = async (pagina = 1) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/interacta/info-percorsi`, {
                params: {
                    page: pagina,
                    limit,
                    fromDate: fromDate || undefined,
                    toDate: toDate || undefined,
                    nomePercorso: percorso,
                    targa,
                    stati: stati.join(","),
                    sortKey: sortKeyMain,
                    sortDir: sortDirMain,
                },
            });
            const risultati = res.data?.data ?? [];
            const paginazione = res.data?.pagination ?? { totalPages: 1, currentPage: 1 };

            setDati(risultati);
            setPagineTotali(paginazione.totalPages);
            setPaginaCorrente(paginazione.currentPage);
            setTotaleRisultati(paginazione.totalItems ?? 0);
        } catch (err) {
            console.error("Errore nel fetch:", err);
            setDati([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(paginaCorrente);
        fetchStats();
        // eslint-disable-next-line
    }, [paginaCorrente, limit, sortKeyMain, sortDirMain]);

    const totalPages = Math.ceil(totaleRisultati / limit);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
            alert("La data iniziale non può essere successiva alla data finale.");
            return;
        }
        setPaginaCorrente(1);
        fetchData(1);
        fetchStats();
    };

    const handleReset = () => {
        setFromDate("");
        setToDate("");
        setPercorso("");
        setTarga("");
        setStati(["Completato", "In lavorazione"]);
        setSearchTerm("");
        setPaginaCorrente(1);
        fetchData(1);
        fetchStats();
    };

    const handleMainSort = (key) => {
        setSortKeyMain((prevKey) => {
            if (prevKey === key) {
                setSortDirMain((d) => (d === "asc" ? "desc" : "asc"));
                return prevKey;
            } else {
                setSortDirMain(key === "data" ? "desc" : "asc");
                return key;
            }
        });
    };

    const handleSort = (key) => {
        if (key === sortKey) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    const handleMainSortExplicit = (key, dir) => {
        setSortKeyMain(key);
        setSortDirMain(dir);
    };

    const handleSortExplicit = (key, dir) => {
        setSortKey(key);
        setSortDir(dir);
    };

    const dirFactor = (sortDir === "asc" ? 1 : -1);

    const estraiCampane = (percorso) => {
        const campane = (percorso.campane || []).filter(c => c.id);

        const cmpPlanning = (a, b) => {
            const oa = a.relative_order ?? Number.MAX_SAFE_INTEGER;
            const ob = b.relative_order ?? Number.MAX_SAFE_INTEGER;
            if (oa !== ob) return (oa - ob) * dirFactor;
            return String(a.nome || "").localeCompare(String(b.nome || "")) * dirFactor;
        };

        const cmpDate = (a, b) => {
            const da = a.data_svuotamento ? new Date(a.data_svuotamento) : null;
            const db = b.data_svuotamento ? new Date(b.data_svuotamento) : null;

            if (da && db) {
                const diff = da - db;
                if (diff !== 0) return diff * dirFactor;
                return ((a.relative_order ?? 1e15) - (b.relative_order ?? 1e15)) * dirFactor;
            }
            if (da && !db) return (sortDir === "asc" ? -1 : 1);
            if (!da && db) return (sortDir === "asc" ? 1 : -1);
            return 0;
        };

        const comparator = sortKey === "date" ? cmpDate : cmpPlanning;
        return campane.slice().sort(comparator);
    };

    const handlePageChange = (nuovaPagina) => {
        if (nuovaPagina >= 1 && nuovaPagina <= pagineTotali) {
            setPaginaCorrente(nuovaPagina);
        }
    };

    const filteredDati = dati.filter(row => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            row.percorso?.toLowerCase().includes(search) ||
            row.targa?.toLowerCase().includes(search) ||
            row.stato_percorso?.toLowerCase().includes(search)
        );
    });

    return (
        <div className="info-percorsi-container">
            <header className="page-header">
                <div className="header-content">
                    <button className="back-button" onClick={() => navigate('/produttivita')}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 12H5M12 19l-7-7 7-7"/>
                        </svg>
                        Indietro
                    </button>
                    <div className="header-title">
                        <div className="header-title-row">
                            <h2>Info Percorsi</h2>
                            <button
                                className="info-button-modern"
                                onClick={() => setShowInfo(!showInfo)}
                                title="Informazioni"
                            >
                                <span className="info-icon-text">i</span>
                            </button>
                        </div>
                        <p className="header-subtitle">Monitoraggio percorsi Interacta con dettaglio campane</p>
                    </div>
                </div>
            </header>

            {/* Modal Informazioni */}
            {showInfo && (
                <div className="modal-overlay" onClick={() => setShowInfo(false)}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <div className="modal-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 11l3 3L22 4"></path>
                                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                                </svg>
                            </div>
                            <h3>Informazioni sulla pagina</h3>
                            <button className="modal-close" onClick={() => setShowInfo(false)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-section">
                                <h4>Cosa fa questa pagina?</h4>
                                <p>
                                    Questa sezione mostra l'elenco dei <strong>percorsi Interacta</strong> in stato{" "}
                                    <strong>"in lavorazione"</strong> o <strong>"completati"</strong>.
                                </p>
                            </div>
                            <div className="modal-section">
                                <h4>Informazioni visualizzate</h4>
                                <p>Per ogni percorso vengono visualizzati:</p>
                                <ul style={{ marginLeft: '20px', marginTop: '10px', lineHeight: '1.8' }}>
                                    <li>La <strong>targa</strong> del mezzo coinvolto</li>
                                    <li>Il <strong>numero totale di campane</strong> assegnate</li>
                                    <li>Il numero di campane <strong>svuotate</strong> e <strong>da svuotare</strong></li>
                                    <li>La <strong>stima delle campane totali raccolte</strong> durante il giro</li>
                                </ul>
                            </div>
                            <div className="modal-section">
                                <h4>Dettaglio campane</h4>
                                <p>
                                    Cliccando su <strong>Dettaglio</strong> si apre una vista delle singole campane, con la{" "}
                                    <strong>data e l'orario di svuotamento</strong> e la{" "}
                                    <strong>percentuale di riempimento</strong> registrata.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="content-area">
                {/* Toggle filtri */}
                <button
                    className={`toggle-filters-btn ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                    </svg>
                    {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </button>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="filters-panel">
                        <form className="filters-form" onSubmit={handleSubmit}>
                            <div className="filters-grid">
                                <div className="filter-item">
                                    <label>Dal</label>
                                    <DatePickerModal
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        max={toDate || undefined}
                                        label="Dal"
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Al</label>
                                    <DatePickerModal
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        min={fromDate || undefined}
                                        label="Al"
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Percorso</label>
                                    <input
                                        type="text"
                                        value={percorso}
                                        onChange={(e) => setPercorso(e.target.value)}
                                        placeholder="Nome percorso..."
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Targa</label>
                                    <input
                                        type="text"
                                        value={targa}
                                        onChange={(e) => setTarga(e.target.value)}
                                        placeholder="Targa..."
                                    />
                                </div>

                                <div className="filter-item">
                                    <label>Stato Percorso</label>
                                    <Select
                                        isMulti
                                        classNamePrefix="rs"
                                        options={[
                                            { value: "Completato", label: "Completato" },
                                            { value: "In lavorazione", label: "In lavorazione" },
                                            { value: "Da eseguire", label: "Da eseguire" },
                                        ]}
                                        value={stati.map((s) => ({ value: s, label: s }))}
                                        onChange={(selected) => setStati(selected.map((s) => s.value))}
                                    />
                                </div>

                                <div className="filter-item filter-actions">
                                    <label>&nbsp;</label>
                                    <div className="filter-buttons">
                                        <button type="submit" className="btn-apply">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                            Applica
                                        </button>
                                        <button type="button" className="btn-reset" onClick={handleReset}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                                                <path d="M3 3v5h5"></path>
                                            </svg>
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {/* Search + View controls */}
                <div className="top-controls">
                    <div className="controls-left">
                        <div className="search-bar-compact">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"></circle>
                                <path d="m21 21-4.35-4.35"></path>
                            </svg>
                            <input
                                type="text"
                                placeholder="Cerca percorso, targa, stato..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="controls-right">
                        <div className="view-toggle-compact">
                            <button
                                className={`view-btn-compact ${viewMode === 'table' ? 'active' : ''}`}
                                onClick={() => setViewMode('table')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 9h18M3 15h18M9 3v18"></path>
                                </svg>
                                Tabella
                            </button>
                            <button
                                className={`view-btn-compact ${viewMode === 'cards' ? 'active' : ''}`}
                                onClick={() => setViewMode('cards')}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="3" width="7" height="7"></rect>
                                    <rect x="14" y="14" width="7" height="7"></rect>
                                    <rect x="3" y="14" width="7" height="7"></rect>
                                </svg>
                                Cards
                            </button>
                        </div>

                        <div className="results-badge-compact">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 11l3 3L22 4"></path>
                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                            </svg>
                            <span>{filteredDati.length} risultati</span>
                        </div>

                    </div>
                </div>

                {/* KPI Stats Cards */}
                <div className="stats-grid-compact">
                    <div className="stat-card-compact stat-primary">
                        <div className="stat-icon-compact">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 11l3 3L22 4"></path>
                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Totale Percorsi</p>
                            <p className="stat-value-compact">{stats.totalPercorsi}</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-info">
                        <div className="stat-icon-compact">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 7h-9"></path>
                                <path d="M14 17H5"></path>
                                <circle cx="17" cy="17" r="3"></circle>
                                <circle cx="7" cy="7" r="3"></circle>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Campane Totali</p>
                            <p className="stat-value-compact">{stats.campaneTotali}</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-success">
                        <div className="stat-icon-compact">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Campane Svuotate</p>
                            <p className="stat-value-compact">{stats.campaneSvuotate}</p>
                        </div>
                    </div>

                    <div className="stat-card-compact stat-warning">
                        <div className="stat-icon-compact">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        </div>
                        <div className="stat-content-compact">
                            <p className="stat-label-compact">Da Svuotare</p>
                            <p className="stat-value-compact">{stats.campaneDaSvuotare}</p>
                        </div>
                    </div>
                </div>

                {/* Data Table */}
                {loading ? (
                    <Lf23Loader />
                ) : filteredDati.length === 0 ? (
                    <div className="empty-state-compact">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                        <h3>Nessun dato disponibile</h3>
                        <p>Non sono stati trovati percorsi con i filtri selezionati</p>
                    </div>
                ) : (
                    <>
                        {viewMode === 'table' && (
                            <>
                            <div className="fullscreen-btn-row">
                                <button
                                    className="fullscreen-btn-compact"
                                    onClick={() => setIsFullscreen(!isFullscreen)}
                                    title={isFullscreen ? 'Esci da tutto schermo' : 'Tutto schermo'}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        {isFullscreen ? (
                                            <>
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        ) : (
                                            <>
                                                <polyline points="15 3 21 3 21 9"></polyline>
                                                <polyline points="9 21 3 21 3 15"></polyline>
                                                <line x1="21" y1="3" x2="14" y2="10"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </>
                                        )}
                                    </svg>
                                </button>
                            </div>
                            <div className={`table-container-compact${isFullscreen ? ' fullscreen-table' : ''}`}>
                                <RotateDevice active={isFullscreen} />
                                {isFullscreen && (
                                    <div className="fullscreen-exit-bar">
                                        <button
                                            className="fullscreen-btn-compact"
                                            onClick={() => setIsFullscreen(false)}
                                            title="Esci da tutto schermo"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="4 14 10 14 10 20"></polyline>
                                                <polyline points="20 10 14 10 14 4"></polyline>
                                                <line x1="14" y1="10" x2="21" y2="3"></line>
                                                <line x1="3" y1="21" x2="10" y2="14"></line>
                                            </svg>
                                        </button>
                                    </div>
                                )}
                                <div className="table-scroll-area">
                                <table className="data-table-compact">
                            <thead>
                                <tr>
                                    <th
                                        style={{ cursor: "pointer" }}
                                        onClick={() => handleMainSort("data")}
                                    >
                                        Data
                                        <span className="sort-icons">
                                            <button
                                                type="button"
                                                className={`sort-btn ${sortKeyMain === "data" && sortDirMain === "asc" ? "active" : ""}`}
                                                onClick={(e) => { e.stopPropagation(); handleMainSortExplicit("data", "asc"); }}
                                            >▲</button>
                                            <button
                                                type="button"
                                                className={`sort-btn ${sortKeyMain === "data" && sortDirMain === "desc" ? "active" : ""}`}
                                                onClick={(e) => { e.stopPropagation(); handleMainSortExplicit("data", "desc"); }}
                                            >▼</button>
                                        </span>
                                    </th>
                                    <th>Percorso</th>
                                    <th
                                        style={{ cursor: "pointer" }}
                                        onClick={() => handleMainSort("targa")}
                                    >
                                        Targa
                                        <span className="sort-icons">
                                            <button
                                                type="button"
                                                className={`sort-btn ${sortKeyMain === "targa" && sortDirMain === "asc" ? "active" : ""}`}
                                                onClick={(e) => { e.stopPropagation(); handleMainSortExplicit("targa", "asc"); }}
                                            >▲</button>
                                            <button
                                                type="button"
                                                className={`sort-btn ${sortKeyMain === "targa" && sortDirMain === "desc" ? "active" : ""}`}
                                                onClick={(e) => { e.stopPropagation(); handleMainSortExplicit("targa", "desc"); }}
                                            >▼</button>
                                        </span>
                                    </th>
                                    <th
                                        style={{ cursor: "pointer" }}
                                        onClick={() => handleMainSort("stato")}
                                    >
                                        Stato
                                        <span className="sort-icons">
                                            <button
                                                type="button"
                                                className={`sort-btn ${sortKeyMain === "stato" && sortDirMain === "asc" ? "active" : ""}`}
                                                onClick={(e) => { e.stopPropagation(); handleMainSortExplicit("stato", "asc"); }}
                                            >▲</button>
                                            <button
                                                type="button"
                                                className={`sort-btn ${sortKeyMain === "stato" && sortDirMain === "desc" ? "active" : ""}`}
                                                onClick={(e) => { e.stopPropagation(); handleMainSortExplicit("stato", "desc"); }}
                                            >▼</button>
                                        </span>
                                    </th>
                                    <th className="numeric-cell"># Campane</th>
                                    <th className="numeric-cell"># Svuotate</th>
                                    <th className="numeric-cell"># Da Svuotare</th>
                                    <th className="numeric-cell">Stima Raccolte</th>
                                    <th style={{ textAlign: 'center' }}>Dettaglio</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDati.map((row, index) => (
                                    <tr key={index}>
                                        <td>{new Date(row.data).toLocaleDateString('it-IT')}</td>
                                        <td><span className="percorso-name">{row.percorso}</span></td>
                                        <td><span className="badge-compact badge-targa">{row.targa}</span></td>
                                        <td>
                                            <span className={`badge-compact ${
                                                row.stato_percorso === 'Completato' ? 'badge-success' :
                                                row.stato_percorso === 'In lavorazione' ? 'badge-warning' :
                                                'badge-info'
                                            }`}>
                                                {row.stato_percorso}
                                            </span>
                                        </td>
                                        <td className="numeric-cell">{row.nr_campane_percorso}</td>
                                        <td className="numeric-cell success-text">{row.nr_campane_svuotate}</td>
                                        <td className="numeric-cell warning-text">{row.nr_campane_da_svuotare}</td>
                                        <td className="numeric-cell">
                                            {row.percentuale_totale !== null && row.percentuale_totale !== undefined
                                                ? `${(parseFloat(row.percentuale_totale) / 100).toFixed(2)}`
                                                : "-"}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button
                                                className="dettaglio-btn-compact"
                                                onClick={() => { setIsFullscreen(false); setDettaglioAperto(index); }}
                                                title="Vedi dettaglio campane"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <polyline points="12 16 16 12 12 8"></polyline>
                                                    <line x1="8" y1="12" x2="16" y2="12"></line>
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                                </div>

                        {/* Pagination (dentro table-container per fullscreen) */}
                        {!loading && filteredDati.length > 0 && (
                    <div className="pagination-compact">
                        <button
                            className="pagination-btn-compact"
                            disabled={paginaCorrente === 1}
                            onClick={() => handlePageChange(paginaCorrente - 1)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M15 18l-6-6 6-6"/>
                            </svg>
                            Prec
                        </button>
                        <div className="pagination-info-compact">
                            <span className="pagination-text-compact">
                                Pag <strong>{paginaCorrente}</strong> di <strong>{totalPages || 1}</strong>
                            </span>
                            <span className="pagination-total-compact">({totaleRisultati} risultati totali)</span>
                        </div>
                        <button
                            className="pagination-btn-compact"
                            disabled={paginaCorrente === totalPages || totalPages === 0}
                            onClick={() => handlePageChange(paginaCorrente + 1)}
                        >
                            Succ
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M9 18l6-6-6-6"/>
                            </svg>
                        </button>

                        <div className="items-per-page-compact">
                            <span className="items-label-compact">Elementi per pagina:</span>
                            <div className="items-buttons-compact">
                                {[10, 20, 50, 100].map((num) => (
                                    <button
                                        key={num}
                                        className={`items-btn-compact ${limit === num ? "active" : ""}`}
                                        onClick={() => {
                                            setLimit(num);
                                            setPaginaCorrente(1);
                                        }}
                                    >
                                        {num}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                        )}
                    </div>
                        </>
                        )}

                        {viewMode === 'cards' && (
                            <div className="cards-grid-compact">
                                {filteredDati.map((row, index) => (
                                    <div className="data-card-compact" key={index}>
                                        <div className="card-header-compact">
                                            <div className="card-title-compact">{row.percorso}</div>
                                            <span className={`badge-compact ${
                                                row.stato_percorso === 'Completato' ? 'badge-success' :
                                                row.stato_percorso === 'In lavorazione' ? 'badge-warning' :
                                                'badge-info'
                                            }`}>
                                                {row.stato_percorso}
                                            </span>
                                        </div>
                                        <div className="card-body-compact">
                                            <div className="card-row-compact">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="2" y="6" width="20" height="12" rx="2"></rect>
                                                    <path d="M22 10h-6"></path>
                                                    <path d="M2 10h6"></path>
                                                </svg>
                                                <strong>Targa:</strong> {row.targa}
                                            </div>
                                            <div className="card-row-compact">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                                </svg>
                                                <strong>Data:</strong> {new Date(row.data).toLocaleDateString('it-IT')}
                                            </div>

                                            <div className="card-divider-compact"></div>

                                            <div className="card-stats-grid">
                                                <div className="card-stat">
                                                    <span className="card-stat-label">Campane Totali</span>
                                                    <span className="card-stat-value">{row.nr_campane_percorso}</span>
                                                </div>
                                                <div className="card-stat success">
                                                    <span className="card-stat-label">Svuotate</span>
                                                    <span className="card-stat-value">{row.nr_campane_svuotate}</span>
                                                </div>
                                                <div className="card-stat warning">
                                                    <span className="card-stat-label">Da Svuotare</span>
                                                    <span className="card-stat-value">{row.nr_campane_da_svuotare}</span>
                                                </div>
                                                <div className="card-stat info">
                                                    <span className="card-stat-label">Stima Raccolte</span>
                                                    <span className="card-stat-value">
                                                        {row.percentuale_totale !== null && row.percentuale_totale !== undefined
                                                            ? `${(parseFloat(row.percentuale_totale) / 100).toFixed(2)}`
                                                            : "-"}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="card-divider-compact"></div>

                                            <button
                                                className="card-detail-btn"
                                                onClick={() => { setIsFullscreen(false); setDettaglioAperto(index); }}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <polyline points="12 16 16 12 12 8"></polyline>
                                                    <line x1="8" y1="12" x2="16" y2="12"></line>
                                                </svg>
                                                Vedi Dettaglio Campane
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                    </>
                )}
            </div>

            {/* Modal Dettaglio Campane (portal per stare sopra fullscreen) */}
            {dettaglioAperto !== null && createPortal(
                <div className="modal-overlay-dettaglio" onClick={() => setDettaglioAperto(null)}>
                    <div className="modal-container-dettaglio" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-dettaglio">
                            <div className="modal-icon-dettaglio">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 7h-9"></path>
                                    <path d="M14 17H5"></path>
                                    <circle cx="17" cy="17" r="3"></circle>
                                    <circle cx="7" cy="7" r="3"></circle>
                                </svg>
                            </div>
                            <h3>Dettaglio Campane - {dati[dettaglioAperto]?.percorso}</h3>
                            <button className="modal-close-dettaglio" onClick={() => setDettaglioAperto(null)}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body-dettaglio">
                            <table className="dettaglio-table">
                                <thead>
                                    <tr>
                                        <th
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleSort("planning")}
                                        >
                                            #
                                            <span className="sort-icons">
                                                <button
                                                    type="button"
                                                    className={`sort-btn ${sortKey === "planning" && sortDir === "asc" ? "active" : ""}`}
                                                    onClick={(e) => { e.stopPropagation(); handleSortExplicit("planning", "asc"); }}
                                                >▲</button>
                                                <button
                                                    type="button"
                                                    className={`sort-btn ${sortKey === "planning" && sortDir === "desc" ? "active" : ""}`}
                                                    onClick={(e) => { e.stopPropagation(); handleSortExplicit("planning", "desc"); }}
                                                >▼</button>
                                            </span>
                                        </th>
                                        <th>Campana</th>
                                        <th>Driver</th>
                                        <th
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleSort("date")}
                                        >
                                            Data Svuotamento
                                            <span className="sort-icons">
                                                <button
                                                    type="button"
                                                    className={`sort-btn ${sortKey === "date" && sortDir === "asc" ? "active" : ""}`}
                                                    onClick={(e) => { e.stopPropagation(); handleSortExplicit("date", "asc"); }}
                                                >▲</button>
                                                <button
                                                    type="button"
                                                    className={`sort-btn ${sortKey === "date" && sortDir === "desc" ? "active" : ""}`}
                                                    onClick={(e) => { e.stopPropagation(); handleSortExplicit("date", "desc"); }}
                                                >▼</button>
                                            </span>
                                        </th>
                                        <th>% Riempimento</th>
                                        <th>Note</th>
                                        <th>HERA</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {estraiCampane(dati[dettaglioAperto]).map((c, idx) => (
                                        <tr key={idx}>
                                            <td className="numeric-cell">{c.relative_order ?? "-"}</td>
                                            <td><strong>{c.nome}</strong></td>
                                            <td>{c.driver || "-"}</td>
                                            <td>{c.data_svuotamento ? new Date(c.data_svuotamento).toLocaleString('it-IT') : "-"}</td>
                                            <td className="numeric-cell">
                                                {c.percentuale !== null && c.percentuale !== undefined ? (
                                                    <span className={`percentage-badge ${c.percentuale > 80 ? 'high' : c.percentuale > 50 ? 'medium' : 'low'}`}>
                                                        {c.percentuale}%
                                                    </span>
                                                ) : "-"}
                                            </td>
                                            <td style={{ fontSize: '12px', color: '#64748B' }}>{c.note || "-"}</td>
                                            <td style={{ textAlign: 'center', fontSize: '16px' }}>
                                                {c.tag_letto === "SI" ? "✅" : c.tag_letto === "PARZIALE" ? "⚠️" : c.tag_letto === "NO" ? "❌" : "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default InfoPercorsi;
